import React from "react";
import PageFrame from "./PageFrame";
import PageHeading from "./PageHeading";
import Abs from "./Abs";

/**
 * Page 09 - Asset Class Coverage Since 2017.
 *
 * Four asset-class columns at x 50, 301, 549 and 797, each 201 wide, with:
 *   rule       y=335  under the "ASSET CLASS - 0N" kicker
 *   rule       y=478  under the closings / volume figures
 *   bar chart  bottom on y=547
 * and a dark band across the foot (x 48..1008, y 672..733) carrying the three totals.
 *
 * The closings and volume figures are per-deck statistics and print a dash until their
 * services exist. The bar charts are NOT statistics - they are the template's own
 * artwork describing the shape of activity in each class, identical on every deck, so
 * their heights are taken straight from the PDF.
 */

const COLUMN_X = [50, 301, 549, 797];
const COLUMN_WIDTH = 201;

const RULE_KICKER_Y = 335;
const RULE_FIGURES_Y = 478;

// Bars sit on y=547, start 16px into the column and repeat on a 20.4px pitch.
const BAR_BASELINE_Y = 547;
const BAR_AREA_HEIGHT = 24;
const BAR_INSET_X = 16;

/**
 * Bar heights in px, straight from the template. `lead` marks the bars it sets in the
 * solid accent green rather than the pale tint.
 */
const BAR_SERIES = {
    'Retail':       {heights: [5, 9, 12, 14, 12, 16, 19, 22], lead: 2},
    'Office':       {heights: [8, 11, 9, 14, 15, 17, 19, 17], lead: 3},
    'Industrial':   {heights: [5, 8, 7, 11, 14, 17, 19, 21], lead: 2},
    'Multi-Family': {heights: [6, 8, 9, 11, 11, 15, 19, 22], lead: 2},
};

export default function AssetClassCoverage({stats, pageNo, totalPages}) {
    const totals = [
        {x: 341, value: stats.totalClosings, label: 'TOTAL CLOSINGS'},
        {x: 562, value: stats.totalVolume, label: 'AGGREGATE VOLUME'},
        {x: 790, value: stats.totalSqftSold, label: 'SQFT SOLD'},
    ];

    const assetClasses = stats.assetClasses.slice(0, 4);

    return (
        <PageFrame fixed pageNo={pageNo} totalPages={totalPages}>
            <PageHeading
                eyebrow='ASSET CLASS COVERAGE  •  SINCE 2017'
                title='A Practice Across '
                titleAccent='Every Class.'
            />

            <Abs x={48} y={108} w={960} className='bov-rule'/>

            <Abs x={46} y={121} w={640}>
                <div className='bov-asset__intro'>
                    Covering Retail, Office, Industrial, + Multi-Family properties - our team has
                    executed across the full spectrum of commercial real estate, with deep
                    specialization in each.
                </div>
            </Abs>

            {assetClasses.map((assetClass, i) => (
                <Abs key={assetClass.name} x={COLUMN_X[i]} y={302} w={COLUMN_WIDTH}>
                    <div className='bov-asset__colKicker'>
                        {`ASSET CLASS  •  0${i + 1}`}
                    </div>
                    <div className='bov-asset__colName' style={{marginTop: '28px'}}>
                        {assetClass.name}
                    </div>

                    <div className='bov-asset__figures' style={{marginTop: '24px'}}>
                        <div>
                            <div className='bov-asset__figure'>{assetClass.closings}</div>
                            <div className='bov-asset__figureLabel'>CLOSINGS</div>
                        </div>
                        <div>
                            <div className='bov-asset__figure'>{splitSuffix(assetClass.volume)}</div>
                            <div className='bov-asset__figureLabel'>VOLUME</div>
                        </div>
                    </div>
                </Abs>
            ))}

            {/* Column rules and bar charts, placed at the template's own coordinates. */}
            {assetClasses.map((assetClass, i) => (
                <React.Fragment key={`furniture-${assetClass.name}`}>
                    <Abs x={COLUMN_X[i]} y={RULE_KICKER_Y} w={COLUMN_WIDTH} className='bov-rule'/>
                    <Abs x={COLUMN_X[i]} y={RULE_FIGURES_Y} w={COLUMN_WIDTH} className='bov-rule'/>
                    <Abs
                        x={COLUMN_X[i] + BAR_INSET_X}
                        y={BAR_BASELINE_Y - BAR_AREA_HEIGHT}
                        w={COLUMN_WIDTH - BAR_INSET_X}
                        h={BAR_AREA_HEIGHT}
                    >
                        <Sparkline series={BAR_SERIES[assetClass.name]}/>
                    </Abs>
                </React.Fragment>
            ))}

            <Abs x={48} y={672} w={960} h={61} className='bov-asset__band'>
                <Abs x={27} y={19}>
                    <div className='bov-asset__bandKicker'>COMBINED TRACK RECORD</div>
                </Abs>
                {totals.map(total => (
                    <Abs key={total.label} x={total.x - 48} y={6} w={230}>
                        <div className='bov-asset__bandValue'>{splitSuffix(total.value)}</div>
                        <div className='bov-asset__bandLabel'>{total.label}</div>
                    </Abs>
                ))}
            </Abs>
        </PageFrame>
    );
}

function Sparkline({series}) {
    if (!series) return null;
    const {heights, lead} = series;
    const firstLead = heights.length - lead;

    return (
        <div className='bov-asset__bars'>
            {heights.map((height, i) => (
                <div
                    key={i}
                    className={`bov-asset__bar${i >= firstLead ? ' bov-asset__bar--lead' : ''}`}
                    style={{height: `${height}px`}}
                />
            ))}
        </div>
    );
}

/** The unit suffix is set in the accent green ("$5.6" + "B", "45.5" + "M"). */
function splitSuffix(value) {
    const match = /^([^A-Za-z]*)([A-Za-z+]+)$/.exec(String(value));
    if (!match) return value;
    return <span>{match[1]}<em>{match[2]}</em></span>;
}
